import type {
  CheckoutSimple,
  ExtendedProductVariant,
  UnitOfMeasure,
  WovarCustomer,
  WovarProduct,
} from "@commercekit/api";
import { ProductStockAvailabilityEnum } from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import { useCart, useLocale } from "@commercekit/remix/index";
import {
  fallbackMoney,
  formatMoneyWithCurrency,
  removeCommonWords,
} from "@commercekit/shared";
import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { useWvAddToCart } from "~/hooks/useDataLayer";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { getDeliveryEstimate } from "~/lib/deliveryEstimate";
import WvProductActionBar from "../molecules/WvProductActionBar";
import WvProductVariantsActionBar from "../molecules/WvProductVariantsActionBar";
import WvProductShippingInfo from "./WvProductShippingInfo";
import { useYotpo } from "~/hooks/useYotpo";
import { SINGLE_CART_LIMIT, useUnitOfMeasure } from "~/hooks/useUnitOfMeasure";

type ExcessCartAlertKind = "excessAddToCart" | "excessStock" | "excessSale";

/**
 * When total product quantity exceeds {@link SINGLE_CART_LIMIT}, choose which PDP alert
 * to show. Stock / sale messages take precedence over the generic cart-cap message when
 * variant quantity also exceeds variant stock (see callers).
 */
function excessAlertWhenOverCartLimit(params: {
  availableForPurchase: boolean;
  variantStock: number;
  variantQtyAfterAdd: number;
  /** Selected UOM's `canAddToCart` — `productUOM` for multi-UOM add, `primaryUOM` for main add. */
  selectedUomCanAddToCart: boolean;
}): ExcessCartAlertKind {
  const {
    availableForPurchase,
    variantStock,
    variantQtyAfterAdd,
    selectedUomCanAddToCart,
  } = params;
  if (
    !availableForPurchase &&
    variantStock > 0 &&
    variantQtyAfterAdd > variantStock
  ) {
    return variantStock < SINGLE_CART_LIMIT ? "excessStock" : "excessAddToCart";
  }
  if (
    selectedUomCanAddToCart &&
    availableForPurchase &&
    variantQtyAfterAdd > variantStock
  ) {
    return variantStock < SINGLE_CART_LIMIT ? "excessStock" : "excessSale";
  }
  return "excessAddToCart";
}

const WvProductVariants: HTMLComponent<{
  product: WovarProduct;
  lineItems?: CheckoutSimple["lineItems"];
  quantityInCart?: number;
  customer?: WovarCustomer;
  setShowExcessAddToCart: (show: boolean) => void;
  setShowExcessStock: (show: boolean) => void;
  setShowExcessSaleAddToCart: (show: boolean) => void;
  updateSelectedProductVariant: (
    selectedProductVariant: ExtendedProductVariant | undefined
  ) => void;
  updateSelectedUomAndQuantity: (uomProperty: string) => void;
}> = ({
  product,
  lineItems,
  quantityInCart,
  customer,
  setShowExcessAddToCart,
  setShowExcessStock,
  setShowExcessSaleAddToCart,
  updateSelectedProductVariant,
  updateSelectedUomAndQuantity,
}) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const { addToCart, loadingQueue, cartLoading } = useCart();
  const yotpo = useYotpo();
  const gtmAddToCart = useWvAddToCart();
  const getUnitOfMeasure = useUnitOfMeasure();

  const allUOMItemName =
    product?.variants && product?.variants?.length > 0
      ? product?.variants[0]?.unitsOfMeasure?.map(
          (data, index) => data?.itemName + index
        )
      : product?.unitsOfMeasure?.map((data, index) => data?.itemName + index);

  const [productCount, setProductCount] = useState(
    allUOMItemName?.reduce((a, v) => ({ ...a, [v]: "1" }), {})
  );
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [selectedUomProperty, setSelectedUomProperty] = useState("");
  const initialVariant =
    product?.variants && product?.variants?.length > 0
      ? product?.variants[selectedVariantIndex]
      : {
          name: product?.name,
          id: product?.id,
          sku: product?.sku,
          defaultImage: product?.defaultImage,
          priceInclTax: product?.priceInclTax,
          priceExclTax: product?.priceExclTax,
          salePriceInclTax: product?.salePriceInclTax,
          salePriceExclTax: product?.salePriceExclTax,
          recommendedPriceInclTax: product?.recommendedPriceInclTax,
          recommendedPriceExclTax: product?.recommendedPriceExclTax,
          stock: product?.stockLevel,
        };

  const [selectedVariant, setSelectedVariant] = useState(initialVariant);

  const initialUOM =
    product?.variants?.length > 0
      ? product?.variants?.filter((x) => x?.sku === selectedVariant?.sku)[0]
          ?.unitsOfMeasure
      : product?.unitsOfMeasure;
  const [selectedVariantUOM, setSelectedVariantUOM] = useState<
    UnitOfMeasure[] | []
  >(initialUOM);

  const primaryUOM = useMemo(() => {
    return product.unitsOfMeasure.find((v) => v.primary);
  }, [product.unitsOfMeasure]);

  /** In primary-UOM–normalized units, for the selected variant only (not summed across siblings). */
  const variantQuantityInCart = useMemo(() => {
    if (!lineItems?.length || !selectedVariant?.sku) {
      return 0;
    }
    return lineItems
      .filter(
        (v) =>
          v?.product?.slug === product?.slug && v?.sku === selectedVariant?.sku
      )
      .reduce((sum, v) => {
        const uomQuantity = Number(
          product.unitsOfMeasure.find((x) => x.uomProperty === v.uomProperty)
            ?.quantity
        );
        return sum + (v?.quantity ?? 0) * uomQuantity;
      }, 0);
  }, [lineItems, product?.slug, product.unitsOfMeasure, selectedVariant?.sku]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { name, value } = target;
    setProductCount({ ...productCount, [name]: value });
  };

  const syncSelectedProductVariantForPopup = useCallback(
    (uomProperty: string, sku: string | null | undefined = selectedVariant?.sku) => {
      if (!sku) {
        return;
      }
      const fullVariant =
        product.variants?.find((x) => x.sku === sku) ||
        (product.sku === sku
          ? ({
              ...selectedVariant,
              unitsOfMeasure: product.unitsOfMeasure,
            } as ExtendedProductVariant)
          : undefined);
      if (!fullVariant) {
        return;
      }
      updateSelectedProductVariant({
        ...fullVariant,
        uomProperty: getUnitOfMeasure(uomProperty) ?? "",
      });
    },
    [
      product.variants,
      product.sku,
      product.unitsOfMeasure,
      selectedVariant,
      updateSelectedProductVariant,
      getUnitOfMeasure,
    ]
  );

  const onSelectedVariantChanged = (sku: string) => {
    const uom =
      product?.variants?.length > 0
        ? product?.variants?.find((x) => x?.sku === sku)?.unitsOfMeasure || []
        : product?.unitsOfMeasure;

    const currentUOMItemName = uom?.map(
      (data, index) => data?.itemName + index
    );
    setProductCount(
      currentUOMItemName?.reduce((a, v) => ({ ...a, [v]: "1" }), {})
    );
    setSelectedVariantUOM(uom);
    syncSelectedProductVariantForPopup(selectedUomProperty, sku);
  };

  // GA4 add_to_cart uses the selected variant/unit price and stock, not the parent's.
  const getGtmAddToCartOverrides = useCallback(
    (uomProperty?: string) => {
      const uom = selectedVariantUOM?.find(
        (x: UnitOfMeasure) => x.uomProperty === uomProperty
      );
      const price = uom
        ? uom.salePriceInclTax?.value ?? uom.priceInclTax?.value
        : selectedVariant?.salePriceInclTax?.value ??
          selectedVariant?.priceInclTax?.value;
      return {
        price: price ?? undefined,
        stockLevel: selectedVariant?.stock ?? undefined,
      };
    },
    [selectedVariantUOM, selectedVariant]
  );

  const addProductToCart = useCallback(
    (itemName: string, uomProperty: string) => {
      setShowExcessAddToCart(false);
      setShowExcessStock(false);
      setShowExcessSaleAddToCart(false);
      const quantity =
        Number((productCount as { [key: string]: any })[`${itemName}`]) > 0
          ? Number((productCount as { [key: string]: any })[`${itemName}`])
          : 1;

      const productUOM = product.unitsOfMeasure.find(
        (x) => x.uomProperty === uomProperty
      );
      const totalAddQuantity =
        (quantity * Number(productUOM?.quantity)) /
        Number(primaryUOM?.quantity);

      const proceedToAdd = () => {
        addToCart({
          ...product,
          sku: selectedVariant?.sku,
          quantity,
          uomProperty,
        });
        gtmAddToCart(
          {
            ...product,
            sku: selectedVariant?.sku,
          },
          quantity,
          getGtmAddToCartOverrides(uomProperty)
        );
        setSelectedUomProperty(uomProperty);
        syncSelectedProductVariantForPopup(uomProperty);
        updateSelectedUomAndQuantity(`${uomProperty}_${quantity}`);
      };

      const totalProductQtyAfterAdd = (quantityInCart ?? 0) + totalAddQuantity;
      const variantQtyAfterAdd = variantQuantityInCart + totalAddQuantity;
      const variantStock = Number(selectedVariant?.stock ?? 0);

      if (totalProductQtyAfterAdd > SINGLE_CART_LIMIT) {
        const kind = excessAlertWhenOverCartLimit({
          availableForPurchase: product.availableForPurchase,
          variantStock,
          variantQtyAfterAdd,
          selectedUomCanAddToCart: Boolean(
            productUOM?.availability.canAddToCart
          ),
        });
        if (kind === "excessStock") {
          setShowExcessStock(true);
        } else if (kind === "excessSale") {
          setShowExcessSaleAddToCart(true);
        } else {
          setShowExcessAddToCart(true);
        }
        return;
      }
      if (!product.availableForPurchase && variantStock > 0) {
        if (variantQtyAfterAdd <= variantStock) {
          proceedToAdd();
        } else {
          if (
            variantQtyAfterAdd > variantStock &&
            variantStock < SINGLE_CART_LIMIT
          ) {
            setShowExcessStock(true);
          } else {
            setShowExcessAddToCart(true);
          }
        }
      } else if (
        productUOM?.availability.canAddToCart &&
        product.availableForPurchase
      ) {
        if (variantQtyAfterAdd > variantStock) {
          if (variantStock < SINGLE_CART_LIMIT) {
            setShowExcessStock(true);
          } else {
            setShowExcessSaleAddToCart(true);
          }
        }
        proceedToAdd();
      } else {
        proceedToAdd();
      }
    },
    [
      productCount,
      product,
      addToCart,
      selectedVariant?.sku,
      selectedVariant?.stock,
      gtmAddToCart,
      getGtmAddToCartOverrides,
      setShowExcessAddToCart,
      setShowExcessStock,
      setShowExcessSaleAddToCart,
      updateSelectedUomAndQuantity,
      syncSelectedProductVariantForPopup,
      quantityInCart,
      variantQuantityInCart,
      primaryUOM?.quantity,
    ]
  );

  const isAddingToCart = useMemo(() => {
    const loading =
      (loadingQueue?.find((i) => i?.id === product?.id) && cartLoading) ||
      false;
    return loading;
  }, [loadingQueue, cartLoading, product]);

  const [mainProductCount, setMainProductCount] = useState("1");

  const addMainProductToCart = useCallback(() => {
    setShowExcessAddToCart(false);
    setShowExcessStock(false);
    setShowExcessSaleAddToCart(false);
    const mainQuantity =
      Number(mainProductCount) > 0 ? Number(mainProductCount) : 1;
    const proceedToAdd = () => {
      setMainProductCount(`${mainQuantity}`);
      addToCart({
        ...product,
        sku: selectedVariant?.sku,
        quantity: mainQuantity,
      });
      gtmAddToCart(
        { ...product, sku: selectedVariant?.sku },
        mainQuantity,
        getGtmAddToCartOverrides()
      );
      syncSelectedProductVariantForPopup(primaryUOM?.uomProperty ?? "");
      updateSelectedUomAndQuantity(
        `${primaryUOM?.uomProperty}_${mainQuantity}`
      );
    };

    const totalProductQtyAfterAdd = (quantityInCart ?? 0) + mainQuantity;
    const variantQtyAfterAdd = variantQuantityInCart + mainQuantity;
    const variantStock = Number(selectedVariant?.stock ?? 0);

    if (totalProductQtyAfterAdd > SINGLE_CART_LIMIT) {
      const kind = excessAlertWhenOverCartLimit({
        availableForPurchase: product.availableForPurchase,
        variantStock,
        variantQtyAfterAdd,
        selectedUomCanAddToCart: Boolean(
          primaryUOM?.availability.canAddToCart
        ),
      });
      if (kind === "excessStock") {
        setShowExcessStock(true);
      } else if (kind === "excessSale") {
        setShowExcessSaleAddToCart(true);
      } else {
        setShowExcessAddToCart(true);
      }
      return;
    }
    if (!product.availableForPurchase && variantStock > 0) {
      if (variantQtyAfterAdd <= variantStock) {
        proceedToAdd();
      } else {
        if (
          variantQtyAfterAdd > variantStock &&
          variantStock < SINGLE_CART_LIMIT
        ) {
          setShowExcessStock(true);
        } else {
          setShowExcessAddToCart(true);
        }
      }
    } else if (
      primaryUOM?.availability.canAddToCart &&
      product.availableForPurchase
    ) {
      if (variantQtyAfterAdd > variantStock) {
        if (variantStock < SINGLE_CART_LIMIT) {
          setShowExcessStock(true);
        } else {
          setShowExcessSaleAddToCart(true);
        }
      }
      proceedToAdd();
    } else {
      proceedToAdd();
    }
  }, [
    mainProductCount,
    product,
    addToCart,
    selectedVariant?.sku,
    selectedVariant?.stock,
    gtmAddToCart,
    getGtmAddToCartOverrides,
    setShowExcessAddToCart,
    setShowExcessStock,
    setShowExcessSaleAddToCart,
    primaryUOM?.availability.canAddToCart,
    primaryUOM?.uomProperty,
    quantityInCart,
    variantQuantityInCart,
    syncSelectedProductVariantForPopup,
    updateSelectedUomAndQuantity,
  ]);

  const variants = product?.variants
    ? removeCommonWords(product?.variants?.map((x) => x?.name))
    : [];

  const notB2B = useMemo(
    () => !customer || customer.type === "B2C",
    [customer]
  );

  const isB2B15 = useMemo(
    () => customer && customer.type.includes("B2B15"),
    [customer]
  );

  return (
    <>
      <div className="position-relative">
        {isB2B15 ? (
          <div className="pdp__order-price price-mini">
            <span className="text dark">{$pt("advice_price_label")}</span>
            <span className="font-weight-bold">
              <span
                className="mx-1"
                data-price={selectedVariant?.recommendedPriceExclTax?.value}
              >
                {formatMoneyWithCurrency(
                  localeFullName,
                  selectedVariant?.recommendedPriceExclTax?.currencyCode
                    ? selectedVariant?.recommendedPriceExclTax
                    : fallbackMoney()
                )}
              </span>
            </span>

            <span className="mt-1 text dark">
              <span>{$pt("product_label_excluding_tax")}</span>
            </span>

            <div className="mt-3 mb-1 text dark font-weight-bold price-mini">
              {$pt("your_price_label")}:
            </div>
          </div>
        ) : null}
        {selectedVariant?.salePriceInclTax &&
        selectedVariant?.salePriceInclTax?.value > 0 ? (
          <div className="pdp__order-price">
            {notB2B ? (
              <div className="price d-flex">
                <div
                  className="mr-2 d-inline-bloc price price--old"
                  data-price={selectedVariant?.priceInclTax?.value}
                >
                  {formatMoneyWithCurrency(
                    localeFullName,
                    selectedVariant?.priceInclTax?.currencyCode
                      ? selectedVariant?.priceInclTax
                      : fallbackMoney()
                  )}
                </div>
                <span
                  className="d-inline"
                  data-price={selectedVariant?.salePriceInclTax?.value}
                >
                  {formatMoneyWithCurrency(
                    localeFullName,
                    selectedVariant?.salePriceInclTax?.currencyCode
                      ? selectedVariant?.salePriceInclTax
                      : fallbackMoney()
                  )}
                </span>
              </div>
            ) : (
              <div className="price d-flex">
                <div
                  className="mr-2 d-inline-bloc price price--old"
                  data-price={selectedVariant?.priceExclTax?.value}
                >
                  {formatMoneyWithCurrency(
                    localeFullName,
                    selectedVariant?.priceExclTax?.currencyCode
                      ? selectedVariant?.priceExclTax
                      : fallbackMoney()
                  )}
                </div>
                <span
                  className="d-inline"
                  data-price={selectedVariant?.salePriceExclTax?.value}
                >
                  {formatMoneyWithCurrency(
                    localeFullName,
                    selectedVariant?.salePriceExclTax?.currencyCode
                      ? selectedVariant?.salePriceExclTax
                      : fallbackMoney()
                  )}
                </span>
              </div>
            )}
            <div className="mt-1 price price--vat">
              <span
                className=""
                data-price={selectedVariant?.salePriceExclTax?.value}
              >
                {notB2B
                  ? formatMoneyWithCurrency(
                      localeFullName,
                      selectedVariant?.salePriceExclTax?.currencyCode
                        ? selectedVariant?.salePriceExclTax
                        : fallbackMoney()
                    )
                  : null}
              </span>{" "}
              <span>{$pt("product_label_excluding_tax")}</span>
            </div>
          </div>
        ) : (
          <div className="pdp__order-price">
            {notB2B ? (
              <div className="price">
                <span
                  className="d-inline"
                  data-price={selectedVariant?.priceInclTax?.value}
                >
                  {formatMoneyWithCurrency(
                    localeFullName,
                    selectedVariant?.priceInclTax?.currencyCode
                      ? selectedVariant?.priceInclTax
                      : fallbackMoney()
                  )}
                </span>
              </div>
            ) : (
              <div className="price">
                <span
                  className="d-inline"
                  data-price={selectedVariant?.priceExclTax?.value}
                >
                  {formatMoneyWithCurrency(
                    localeFullName,
                    selectedVariant?.priceExclTax?.currencyCode
                      ? selectedVariant?.priceExclTax
                      : fallbackMoney()
                  )}
                </span>
              </div>
            )}
            <div className="mt-1 price price--vat">
              <span
                className=""
                data-price={selectedVariant?.priceExclTax?.value}
              >
                {notB2B
                  ? formatMoneyWithCurrency(
                      localeFullName,
                      selectedVariant?.priceExclTax?.currencyCode
                        ? selectedVariant?.priceExclTax
                        : fallbackMoney()
                    )
                  : null}
              </span>{" "}
              <span>{$pt("product_label_excluding_tax")}</span>
            </div>
          </div>
        )}
        <div className="pdp__order-discount position-right position-top position-absolute">
          {isAddingToCart && (
            <i className="zmdi-hc-spin large" data-icon-after=""></i>
          )}
        </div>
      </div>

      <div className="my-3 pdp__order-addtocart">
        {product?.variants?.length > 0 ? (
          <div className="pt-4 my-3 pdp__order-staffel">
            <span>
              <b>{$pt("product_label_variant_selected")}:</b>{" "}
              {variants[selectedVariantIndex]}
            </span>
            <div className="py-1 d-flex align-items-center">
              {product?.variants?.length > 0 &&
                product?.variants?.map((option, index) => (
                  <label
                    key={index}
                    className={`variant-radio-option mr-2 ${
                      option?.sku === selectedVariant?.sku
                        ? "selected-variant"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      value={option?.sku}
                      checked={option?.sku === selectedVariant?.sku}
                      onChange={() => {
                        setSelectedVariant(option);
                        setSelectedVariantIndex(index);
                        onSelectedVariantChanged(option?.sku);
                      }}
                    />
                    {variants[index]}
                  </label>
                ))}
            </div>
          </div>
        ) : null}
        {selectedVariantUOM[0]?.availability?.canAddToCart ? (
          <div className="pt-4 my-3 pdp__order-addtocart pdp__order-staffel">
            <form
              action="/cart/"
              method="post"
              className="product-form product-submit-form"
              data-message=""
            >
              <div className="mb-3 row">
                <div className="col-auto count">
                  <input
                    type="number"
                    name="count"
                    className="form-control"
                    value={mainProductCount}
                    onChange={(e) => {
                      setMainProductCount(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addMainProductToCart();
                      }
                    }}
                    min="1"
                    step="1"
                  />
                </div>
                <div className="px-0 col col-lg-2 col-xxl-2 pdp__order-label d-flex align-items-center">
                  <span className="pr-1">
                    <svg
                      width="18"
                      height="5"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14.829 2.443c-.615.051-1.231.086-1.84.117a52.362 52.362 0 01-4.144-.043c-1.375-.066-2.729-.242-4.094-.411a62.302 62.302 0 01-1.98-.272 192.58 192.58 0 01-.358-.073c.276-.136.556-.249.824-.376.57-.262.16-1.222-.424-1.014-.797.289-1.604.57-2.38.902-.336.143-.63.585-.37.948.585.8 1.164 1.603 1.748 2.407.599.83 1.759-.093 1.105-.898-.251-.306-.502-.618-.757-.93.122.03.232.063.348.092.682.197 1.376.387 2.077.529 1.351.27 2.75.414 4.133.515a32.81 32.81 0 004.292.03 39.79 39.79 0 002.15-.173c.613-.069 1.25-.06 1.782-.403.414-.262.302-.866-.137-1.003-.618-.193-1.334.005-1.975.056"
                        fill="#626262"
                        fillRule="evenodd"
                      ></path>
                    </svg>
                  </span>
                  <span>
                    {getUnitOfMeasure(
                      primaryUOM?.uomProperty ?? "",
                      parseInt(mainProductCount) > 1
                    )}
                  </span>
                </div>
                <div className="mt-3 col-12 col-lg mt-lg-0 pl-lg-0 pl-xl-3">
                  <WvProductActionBar onAddToCart={addMainProductToCart} />
                </div>
              </div>
            </form>
          </div>
        ) : null}
        <div className="pdp__order-delivery-stock">
          <div className="flex-wrap d-flex flex-column">
            <div className="px-2 col-12 col-lg-auto mb-md-0 pb-2">
              {primaryUOM?.availability?.stockAvailabilityMessage ===
                ProductStockAvailabilityEnum.IN_STOCK ||
              primaryUOM?.availability?.stockAvailabilityMessage ===
                ProductStockAvailabilityEnum.SOME_UNITS_AVAILABLE ? (
                <span className="in-stock" data-icon-before="">
                  {primaryUOM?.availability?.stockAvailabilityMessage ===
                  ProductStockAvailabilityEnum.IN_STOCK ? (
                    <>{$pt("product_label_in_stock")}</>
                  ) : null}
                  {primaryUOM?.availability?.stockAvailabilityMessage ===
                  ProductStockAvailabilityEnum.SOME_UNITS_AVAILABLE ? (
                    <>
                      {product?.stockLevel}{" "}
                      {getUnitOfMeasure(
                        primaryUOM?.uomProperty ?? "",
                        product.stockLevel > 1
                      )?.toLowerCase()}{" "}
                      {$pt("product_label_available")}
                    </>
                  ) : null}
                </span>
              ) : (
                <span className="not-in-stock" data-icon-before="">
                  {primaryUOM?.availability?.stockAvailabilityMessage ===
                  ProductStockAvailabilityEnum.OUT_OF_STOCK ? (
                    <>{$pt("product_label_out_of_stock")}</>
                  ) : null}
                  {primaryUOM?.availability?.stockAvailabilityMessage ===
                  ProductStockAvailabilityEnum.IMPOSSIBLE_TO_ORDER ? (
                    <>{$pt("product_label_impossible_to_order")}</>
                  ) : null}
                </span>
              )}
            </div>
            {primaryUOM?.availability?.stockAvailabilityMessage !==
            ProductStockAvailabilityEnum.IMPOSSIBLE_TO_ORDER ? (
              <div className="px-2 col-12 col-lg-auto">
                <span
                  className="truck"
                  {...(product.stockLevel > 0 && {
                    "data-icon-after": "",
                  })}
                >
                  {getDeliveryEstimate({
                    $pt,
                    deliveryEstimate:
                      primaryUOM?.availability?.deliveryEstimateMessage,
                    deliveryDate: primaryUOM?.availability?.deliveryDate,
                    workingDays: primaryUOM?.availability?.workingDays,
                    defaultShipping: primaryUOM?.defaultShipping,
                  })}
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <WvProductShippingInfo product={product} notB2B={notB2B} />
        {selectedVariantUOM && selectedVariantUOM?.length > 0 ? (
          <div className="pt-4 my-3 pdp__order-staffel">
            <h3 className="heading sm sans-serif strong">
              {$pt("product_label_volume_discount_title")}
            </h3>

            <div className="py-2 d-flex no-gutters variants-table-headers">
              <div className="pl-3 pr-1 col-4">
                {$pt("product_label_volume_discount_no")}
              </div>
              <div className="px-1 col-4">
                {$pt("product_label_volume_discount_price")}
                <small>
                  (
                  {notB2B
                    ? $pt("product_label_including_tax")
                    : $pt("product_label_excluding_tax")}
                  )
                </small>
              </div>
              <div className="px-1 col-2">
                {$pt("product_label_volume_disc_percent")}
              </div>
              <div className="px-1 col-2">
                {$pt("product_label_volume_fee_percent")}
              </div>
            </div>
            {selectedVariantUOM
              .filter((x) => !x?.primary)
              .map((item, index) => (
                <div
                  key={item?.itemName + index}
                  className="d-flex flex-column"
                >
                  <div className="pt-1 d-flex no-gutters">
                    <div className="py-2 pl-3 pr-1 col-4 amount">
                      {item?.uomProperty === "Piece" ? (
                        <strong>{getUnitOfMeasure(item?.uomProperty)}</strong>
                      ) : (
                        <>
                          <strong>{getUnitOfMeasure(item?.uomProperty)}</strong>
                          <span className="pl-1">
                            {`(${Number(item?.quantity).toFixed(
                              0
                            )} ${getUnitOfMeasure(
                              product.unitsOfMeasure.find((v) => v.primary)
                                ?.uomProperty ?? "",
                              product.stockLevel / Number(item?.quantity) > 1
                            )})`}
                          </span>
                        </>
                      )}
                    </div>
                    {notB2B ? (
                      <>
                        {" "}
                        {item?.salePriceInclTax &&
                        Number(item?.discount) > 0 ? (
                          <div
                            className="px-1 py-2 col-4 staffelprice"
                            data-price={item?.salePriceInclTax?.value}
                          >
                            <span className="price--old">
                              {formatMoneyWithCurrency(
                                localeFullName,
                                item?.priceInclTax?.currencyCode
                                  ? item?.priceInclTax
                                  : fallbackMoney()
                              )}
                            </span>{" "}
                            <span>
                              {formatMoneyWithCurrency(
                                localeFullName,
                                item?.salePriceInclTax?.currencyCode
                                  ? item?.salePriceInclTax
                                  : fallbackMoney()
                              )}
                            </span>
                          </div>
                        ) : (
                          <div
                            className="px-1 py-2 col-4 staffelprice"
                            data-price={item?.priceInclTax?.value}
                          >
                            {formatMoneyWithCurrency(
                              localeFullName,
                              item?.priceInclTax?.currencyCode
                                ? item?.priceInclTax
                                : fallbackMoney()
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {item?.salePriceExclTax &&
                        Number(item?.discount) > 0 ? (
                          <div
                            className="px-1 py-2 col-4 staffelprice"
                            data-price={item?.salePriceExclTax?.value}
                          >
                            <span className="price--old">
                              {formatMoneyWithCurrency(
                                localeFullName,
                                item?.priceExclTax?.currencyCode
                                  ? item?.priceExclTax
                                  : fallbackMoney()
                              )}
                            </span>{" "}
                            <span>
                              {formatMoneyWithCurrency(
                                localeFullName,
                                item?.salePriceExclTax?.currencyCode
                                  ? item?.salePriceExclTax
                                  : fallbackMoney()
                              )}
                            </span>
                          </div>
                        ) : (
                          <div
                            className="px-1 py-2 col-4 staffelprice"
                            data-price={item?.priceExclTax?.value}
                          >
                            {formatMoneyWithCurrency(
                              localeFullName,
                              item?.priceExclTax?.currencyCode
                                ? item?.priceExclTax
                                : fallbackMoney()
                            )}
                          </div>
                        )}
                      </>
                    )}
                    <div className="px-1 py-2 col-2 percentage text-success">
                      {Number(item?.discount.replace(",", ".")) >= 0 && (
                        <span>
                          {Math.abs(
                            Number(item?.discount.replace(",", "."))
                          ).toFixed(1)}
                          %
                        </span>
                      )}
                    </div>
                    <div className="px-1 py-2 col-2 percentage fee">
                      {Number(item?.discount.replace(",", ".")) < 0 && (
                        <span>
                          {Math.abs(
                            Number(item?.discount.replace(",", "."))
                          ).toFixed(1)}
                          %
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedVariantUOM[0]?.availability?.canAddToCart ? (
                    <div className="pb-2 pr-2 d-flex justify-content-between align-items-center">
                      <span
                        className="py-1 pl-3 mr-4 text--success text-break delivery-estimate"
                        data-icon-before=""
                      >
                        {getDeliveryEstimate({
                          $pt,
                          deliveryEstimate:
                            selectedVariant?.stock < parseInt(item.quantity)
                              ? "DELIVERED_IN_X_WORKING_DAYS"
                              : (!selectedVariant?.stock ||
                              selectedVariant?.stock >= parseInt(item.quantity)
                                ? selectedVariantUOM[0]
                                : item
                                )?.availability?.deliveryEstimateMessage,
                          deliveryDate: (!selectedVariant?.stock ||
                          selectedVariant?.stock >= parseInt(item.quantity)
                            ? selectedVariantUOM[0]
                            : item
                            )?.availability?.deliveryDate,
                          workingDays: (!selectedVariant?.stock ||
                          selectedVariant?.stock >= parseInt(item.quantity)
                            ? selectedVariantUOM[0]
                            : item
                            )?.availability?.workingDays,
                          defaultShipping: primaryUOM?.defaultShipping,
                        })}
                      </span>

                      <div className="product-form product-submit-form">
                        <div className="d-flex justify-content-end no-gutters">
                          <div className="col-auto variant-count pr-lg-1 order-lg-1">
                            {/* value={mainProductCount}
                          onChange=
                          {(e) => {
                            setMainProductCount(e.target.value);
                          }}
                          onKeyDown=
                          {(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addMainProductToCart();
                            }
                          }} */}
                            <input
                              type="number"
                              name={item?.itemName + index}
                              className="form-control form-control--small"
                              value={
                                (productCount as { [key: string]: any })[
                                  `${item?.itemName + index}`
                                ]
                              }
                              onChange={(e) => handleChange(e)}
                              onKeyDown={({ key }) => {
                                if (key === "Enter")
                                  addProductToCart(
                                    item?.itemName + index,
                                    item?.uomProperty
                                  );
                              }}
                              min="1"
                              step="1"
                            />
                          </div>
                          {/* TODO: Look into "productInfo" to ensure that it
                        contains the selected uomProperty when the product was
                        added to cart before the popup was opened. */}
                          <div className="col-auto order-lg-2">
                            <WvProductVariantsActionBar
                              newItemName={item?.itemName + index}
                              newUomProperty={item?.uomProperty}
                              newUomQuantity={item?.quantity}
                              isInActive={
                                !selectedVariantUOM[0]?.availability
                                  ?.canAddToCart
                              }
                              isVariantLoading={
                                (loadingQueue?.find(
                                  (i) => i?.uomProperty === item?.uomProperty
                                ) &&
                                  cartLoading) ||
                                false
                              }
                              onAddToCart={addProductToCart}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}

            <div className="mt-3 mb-2 pdp__order-staffel">&nbsp;</div>
            <div className="mt-3 row">
              <div className="col price-disclaimer">
                {notB2B
                  ? $pt("product_label_price_are_incl_tax")
                  : $pt("product_label_price_are_excl_tax")}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};
export default WvProductVariants;
